import { HttpClient } from '@angular/common/http';
import { APP_INITIALIZER, FactoryProvider, Inject, Injectable, Optional } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterEvent } from '@angular/router';

import {
  Observable,
  catchError,
  defer,
  distinctUntilChanged,
  filter,
  fromEventPattern,
  lastValueFrom,
  map,
  mergeMap,
  of,
  race,
  shareReplay,
  startWith,
  take,
  tap,
  throwError,
  timer,
  zip
} from 'rxjs';

import { CommonRoutes, RouteData } from '@hdms/shared/common-types';
import {
  EnvironmentService,
  FeatureToggleService,
  HostContext,
  PlatformService,
  RouterHelperService,
  SERVER_REQUEST,
  ServerRequest,
  TokenService,
  WindowRefService,
  b64EncodeUnicode,
  generateQueryParamString,
  removeEmptyObjectProps,
  sanitizeUrl
} from '@hdms/shared/util-core';

import { FT_NGC_TOKEN_SERVICE_WORKER } from '../toggles';
import { UserContextService } from '../user';

import { ContextIds, LogoutConfig, OauthUserType, RequestParam, StateParam } from './oauth.types';
import {
  LOGIN_URLS,
  LOGOUT_DATA_URL,
  LOGOUT_REDIRECT_URL,
  REFRESH_URLS,
  SILENT_LOGIN_URLS,
  SWITCH_ACCOUNT_URL
} from './oauth.urls';

const IGNORED_ROUTES = [CommonRoutes.EMPTY, CommonRoutes.GENERIC_ERROR].map(r => `/${r}`);

interface RefreshAuthResponse {
  token: string;
}

/**
 * Take first NavigationEnd, which is the final one on server side
 * @param event
 */
function isFirstNavigationEnd(event: RouterEvent): boolean {
  return event.id === 1 && event instanceof NavigationEnd;
}

/**
 * Take either initial (faked) NavigationStart or subsequent ones,
 * which are no internal redirects to error/empty route
 * @param event
 */
function isValidNavigationStart(event: RouterEvent): boolean {
  return event instanceof NavigationStart && (event.id === null || !IGNORED_ROUTES.includes(event.url));
}

function base64Encode(data: StateParam): string {
  return b64EncodeUnicode(JSON.stringify(data));
}

interface UserDetailsResult {
  userAuthentication: boolean;
  accountSynchronized: boolean;
  email?: string;
  csrfToken: string;
  error: boolean;
}

interface ExtendedWindow extends Window {
  aemUserDetailsLoader: {
    registerCallback: (cb: (res: UserDetailsResult) => void) => void;
  };
}

@Injectable({
  providedIn: 'root'
})
export class OauthService {
  private readonly win: ExtendedWindow;
  private readonly state$: Observable<string>;
  private readonly routeData$ = this.getRouteData$();
  private readonly refreshUrls = this.ftService.getToggleById(FT_NGC_TOKEN_SERVICE_WORKER)
    ? REFRESH_URLS
    : SILENT_LOGIN_URLS;

  private userType: OauthUserType;

  constructor(
    @Optional() @Inject(SERVER_REQUEST) private readonly request: ServerRequest,
    private readonly envService: EnvironmentService,
    private readonly userContext: UserContextService,
    private readonly platform: PlatformService,
    private readonly router: Router,
    private readonly routerHelper: RouterHelperService,
    private readonly httpClient: HttpClient,
    private readonly tokenService: TokenService,
    private readonly ftService: FeatureToggleService,
    { nativeWindow }: WindowRefService<ExtendedWindow>
  ) {
    this.win = nativeWindow;
    this.state$ = this.createState$();
    this.setUserType();
  }

  /**
   * Returns login url with up-to-date base64 encoded state param
   */
  public getLoginUrl$(type = this.userType): Observable<string> {
    const { customerId, contactId } = this.getContextIds(type);
    const url = LOGIN_URLS.get(type);

    return this.state$.pipe(map(state => this.generateUrlWithParams(url, state, customerId, contactId)));
  }

  /**
   * Returns logout config: url and state
   *
   * In case route data contains `ssoLogoutRedirectRoute`, the value
   * will be applied to `state` for redirecting after logout.
   * In all other cases, `state` will be set to the current location.
   */
  public getLogoutConfig$(): Observable<LogoutConfig> {
    return this.routeData$.pipe(
      map(
        ({ ssoRedirectRoute, ssoLogoutRedirectRoute }) =>
          ssoRedirectRoute || ssoLogoutRedirectRoute || this.getLocation()
      ),
      distinctUntilChanged(),
      map(redirectUrl => ({
        url: LOGOUT_DATA_URL,
        state: this.createStateParam(LOGOUT_REDIRECT_URL + redirectUrl)
      })),
      shareReplay(1)
    );
  }

  /**
   * Return switch-account url containing provided customer originId
   * @param originId
   */
  public getAccountSwitchUrl$(originId: string): Observable<string> {
    return this.state$.pipe(map(state => this.generateUrlWithParams(SWITCH_ACCOUNT_URL, state, originId)));
  }

  /**
   * In AEM context block app init unless data via HOL userDetails.json endpoint is loaded.
   * Check will run in browser only, as potentially never resolving would hang SSR.
   */
  public syncAemLogin(): Promise<boolean> | true {
    if (this.platform.isBrowser && this.envService.hostContext === HostContext.AEM) {
      return lastValueFrom(
        fromEventPattern<UserDetailsResult>(handler => this.win.aemUserDetailsLoader.registerCallback(handler)).pipe(
          take(1),
          tap(({ userAuthentication, accountSynchronized, email, csrfToken, error }) => {
            if (error) {
              throw new Error();
            }
            this.envService.legacyEnv.loggedIn = userAuthentication;
            this.envService.legacyEnv.isAccountSync = !accountSynchronized && !!email;
            this.tokenService.csrfToken = csrfToken;
          }),
          map(_ => true),
          catchError(_ => {
            this.userContext.forceErrorState();
            return of(true);
          })
        )
      );
    }

    return true;
  }

  /**
   * Invoke silent token refresh request.
   * Returns error observable, in case refreshUrl is unset.
   * @public
   */
  public refreshAuthToken$(): Observable<string> {
    const refreshUrl = this.getRefreshUrl();

    return refreshUrl
      ? this.httpClient
          .get<RefreshAuthResponse>(refreshUrl, {
            withCredentials: true
          })
          .pipe(
            map(({ token }) => token),
            tap(token => {
              this.tokenService.bearerToken = token;
            })
          )
      : throwError(() => new Error('refresh-url unset'));
  }

  /**
   * Returns refresh url with current customer originId.
   * In case logged in as account manager or eVoucher backoffice user,
   * use a dedicated url. For AM also add contact originId.
   * Returned url will be null in case customer originId is unset.
   */
  private getRefreshUrl(): string {
    const { customerId, contactId } = this.getContextIds();
    const { isEVoucherBackofficeUser, isPendingRegistration } = this.userContext;

    return customerId || isEVoucherBackofficeUser || isPendingRegistration
      ? this.generateUrlWithParams(
          this.refreshUrls.get(this.userType),
          this.getRefreshUrlState(),
          customerId,
          contactId
        )
      : null;
  }

  private getRefreshUrlState(): string {
    return this.envService.oauthClientRedirectHost ? this.createStateParam('') : null;
  }

  /**
   * Create state observable returning a base64-encoded state object.
   * State has to be regenerated on every route change to match with actual browser location.
   * As encoding is expensive the observable is created once and replays latest value.
   * The state object will already be generated on NavigationStart, to get the target url
   * before potential route guards would invoke a redirect.
   * In case of late router events subscriptions, defer observable creation
   * to ensure `startWith` will take the most recent location.
   * In case `ssoRedirectRoute` is configured in `RouteData`,
   * this value will have precedence over current route url.
   * @private
   */
  private createState$(): Observable<string> {
    return zip(
      defer(() =>
        this.router.events.pipe(
          startWith(new NavigationStart(null, this.getLocation())),
          filter<RouterEvent>((event: RouterEvent) => isFirstNavigationEnd(event) || isValidNavigationStart(event)),
          map(({ url }: RouterEvent) => (this.platform.isBrowser ? url : this.getLocation())),
          distinctUntilChanged()
        )
      ),
      // Guards waiting for GraphQL responses can block the NavigationEnd event to happen.
      // In this case nothing else is possible as to skip routeData$ after some waiting time.
      race(this.routeData$.pipe(map(({ ssoRedirectRoute }) => ssoRedirectRoute)), timer(100))
    ).pipe(
      map(([routeUrl, redirectUrl]) => this.createStateParam(redirectUrl || routeUrl || '/')),
      shareReplay(1)
    );
  }

  private getRouteData$(): Observable<RouteData> {
    return this.routerHelper.activatedRoute$.pipe(mergeMap(({ data }) => data));
  }

  private generateUrlWithParams(url: string, state?: string, customerId?: string, contactId?: string): string {
    return `${url}?${this.generateParams(state, customerId, contactId)}`;
  }

  private generateParams(state?: string, customerId?: string, contactId?: string): string {
    return generateQueryParamString({
      [RequestParam.STATE]: state,
      [RequestParam.CUSTOMER_ID]: customerId,
      [RequestParam.CONTACT_ID]: contactId,
      [RequestParam.COUNTRY]: this.envService.country,
      [RequestParam.LANG]: this.envService.locale
    });
  }

  /**
   * Return current pathname including query params and fragment
   * @private
   */
  private getLocation(): string {
    const { href, origin } = this.win.location;
    return href.replace(origin, '');
  }

  private setUserType(): void {
    this.userType = this.getUserType();
  }

  private getUserType(): OauthUserType {
    const { isAccountManager, isEVoucherBackofficeUser } = this.userContext;

    switch (true) {
      case isAccountManager:
        return OauthUserType.AM;
      case isEVoucherBackofficeUser:
        return OauthUserType.EVOUCHER;
      default:
        return OauthUserType.DEFAULT;
    }
  }

  private getContextIds(type = this.userType): ContextIds {
    const {
      customerOriginId: customerId,
      contactOriginId: contactId,
      preselectedShipToId: shipToId
    } = this.userContext;

    switch (type) {
      case OauthUserType.AM:
        return { customerId, contactId, shipToId };
      case OauthUserType.EVOUCHER:
        return {};
      default:
        return { customerId };
    }
  }

  /**
   * Create state param object, always containing country and redirect url.
   * @param url
   * @private
   */
  private createStateParam(url: string): string {
    const { queryParams: query, fragment } = this.router.parseUrl(url);

    return base64Encode(
      removeEmptyObjectProps<StateParam>({
        targetUrl: {
          countryCode: this.envService.country,
          relativeUrl: sanitizeUrl(url),
          redirectHost: this.getRedirectHost(),
          query,
          fragment
        }
      })
    );
  }

  /**
   * return current hostname as redirect host in case related
   * env variable is configured on local or staging envs.
   * @private
   */
  private getRedirectHost(): string {
    return this.envService.oauthClientRedirectHost && (this.request?.hostname || this.win.location.hostname);
  }
}

export const OAUTH_LOGIN_SYNC_PROVIDER: FactoryProvider = {
  provide: APP_INITIALIZER,
  useFactory(oauthService: OauthService) {
    return () => oauthService.syncAemLogin();
  },
  deps: [OauthService],
  multi: true
};
